import React, { useState } from "react";
import api from "../lib/api";

/**
 * Age gate shown before the first video upload (least friction).
 *   - under_13  => block account creation, ask for parent/guardian
 *   - 13_17     => allow with reminder about parent/coach permission
 *   - 18plus    => allow
 */
export default function AgeGateModal({ sessionId, onPassed, onCancel }) {
  const [busy, setBusy] = useState(false);
  const [blocked, setBlocked] = useState(false);

  async function pick(band) {
    setBusy(true);
    try {
      const { data } = await api.post("/web/age-band", { session_id: sessionId, age_band: band });
      if (data?.blocked) {
        setBlocked(true);
      } else {
        onPassed?.(band);
      }
    } catch {
      // Soft fail — let user proceed if logging fails so we don't block flow.
      onPassed?.(band);
    } finally {
      setBusy(false);
    }
  }

  if (blocked) {
    return (
      <div className="fixed inset-0 z-50 bg-black/75 flex items-end sm:items-center justify-center p-0 sm:p-4"
           data-testid="age-gate-blocked">
        <div className="w-full sm:max-w-md bg-[#111] border border-neutral-800 rounded-t-3xl sm:rounded-3xl p-6"
             style={{ paddingBottom: "calc(1.5rem + var(--gully-bottom-nav-h, env(safe-area-inset-bottom, 0px)))" }}>
          <div className="text-base font-medium mb-2">Bring in a parent or guardian</div>
          <div className="text-sm text-neutral-300 leading-relaxed mb-5">
            Gully is built for cricketers 13 and over. Ask a parent, guardian or coach to help you set up
            an account together — they can use Gully with you for now.
          </div>
          <button
            onClick={onCancel}
            className="w-full py-3 rounded-xl bg-neutral-900 border border-neutral-800 hover:bg-neutral-800"
            data-testid="age-gate-blocked-close"
          >
            Close
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/75 flex items-end sm:items-center justify-center p-0 sm:p-4"
         data-testid="age-gate-modal">
      <div className="w-full sm:max-w-md bg-[#111] border border-neutral-800 rounded-t-3xl sm:rounded-3xl p-6"
           style={{ paddingBottom: "calc(1.5rem + var(--gully-bottom-nav-h, env(safe-area-inset-bottom, 0px)))" }}>
        <div className="text-base font-medium mb-2">Quick check — your age</div>
        <div className="text-sm text-neutral-400 mb-5">So we keep Gully safe for younger cricketers.</div>
        <div className="space-y-2">
          {[
            { id: "under_13", label: "Under 13" },
            { id: "13_17", label: "13 – 17" },
            { id: "18plus", label: "18 or older" },
          ].map((opt) => (
            <button
              key={opt.id}
              onClick={() => pick(opt.id)}
              disabled={busy}
              className="w-full py-3 rounded-xl border border-neutral-800 hover:border-emerald-700 hover:bg-emerald-950/30 text-left px-4 text-sm disabled:opacity-50"
              data-testid={`age-gate-${opt.id}`}
            >
              {opt.label}
            </button>
          ))}
        </div>
        <button
          onClick={onCancel}
          disabled={busy}
          className="mt-4 w-full text-xs text-neutral-500 hover:text-neutral-300"
          data-testid="age-gate-cancel"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}
